<script setup lang="ts" generic="TValue extends AcceptableInputValue">
import { ChevronDown, ChevronUp } from 'lucide-vue-next'
import { CloseIcon } from 'mdi-vue3'
import type { AcceptableInputValue } from 'reka-ui'
import { computed, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'

import AComboboxAnchor from '../ACombobox/AComboboxAnchor.vue'
import AComboboxEmpty from '../ACombobox/AComboboxEmpty.vue'
import AComboboxGroup from '../ACombobox/AComboboxGroup.vue'
import AComboboxList from '../ACombobox/AComboboxList.vue'
import AComboboxRoot from '../ACombobox/AComboboxRoot.vue'
import AComboboxTrigger from '../ACombobox/AComboboxTrigger.vue'
import AMultiSelectSearch from './AMultiSelectSearch.vue'

export type AMultiSelectRootProperties = {
  maxCount?: number
  placeholder?: string
}

export interface AMultiSelectRootEmits {
  (event: 'search:enter'): void
  (event: 'undo'): void
}

const { t } = useI18n({
  messages: {
    nl: {
      'Select option': 'Selecteer optie',
    },
    en: {
      'Select option': 'Select option',
    },
  },
})

withDefaults(defineProps<AMultiSelectRootProperties>(), {
  maxCount: 3,
  placeholder: undefined,
})

const emit = defineEmits<AMultiSelectRootEmits>()

const modelValue = defineModel<TValue[]>('modelValue', { required: true, default: [] })

function handleKeyDown(event: KeyboardEvent) {
  if (event.ctrlKey && event.key === 'z') emit('undo')
  if (event.metaKey && event.key === 'z') emit('undo')
}

const search = defineModel<string | undefined>('search', { required: true, default: undefined })

const open = ref<boolean>(false)

const uniqueSelectId = ref<string>(`combobox-${useId()}`)

function handleSearchBackspace(): void {
  if (search.value && search.value?.length > 0) return

  modelValue.value?.pop()
  // Reset the modelValue to trigger reactivity
  modelValue.value = [...(modelValue.value || [])]
}

function handleEnter(): void {
  emit('search:enter')
}

function onClear(): void {
  modelValue.value = []
}

const contextProperties = computed(() => {
  return {
    modelValue: modelValue.value,
    search: search.value,
    uniqueSelectId: uniqueSelectId.value,
  }
})
</script>

<template>
  <AComboboxRoot :id="uniqueSelectId" ref="combobox" v-model="modelValue" v-model:open="open" by="label" class="w-full" ignore-filter multiple>
    <AComboboxAnchor as-child>
      <AComboboxTrigger as-child>
        <button class="atabix-multiselect-trigger" tabindex="0">
          <span v-if="!modelValue || modelValue.length === 0" class="atabix-multiselect-placeholder">
            {{ t('Select option') }}
          </span>

          <slot v-else name="tags" v-bind="contextProperties"></slot>

          <div class="atabix-multiselect-actions">
            <ChevronDown v-if="!open" class="atabix-multiselect-action" />
            <ChevronUp v-else class="atabix-multiselect-action" />
            <CloseIcon class="atabix-multiselect-action" @click.prevent="onClear" />
          </div>
        </button>
      </AComboboxTrigger>
    </AComboboxAnchor>

    <AComboboxList>
      <AMultiSelectSearch v-model="search" @keydown="handleKeyDown" @keydown:backspace="handleSearchBackspace" @keydown:enter="handleEnter" />

      <AComboboxEmpty>
        <slot name="empty" v-bind="contextProperties">
          {{ t('No option found.') }}
        </slot>
      </AComboboxEmpty>

      <AComboboxGroup>
        <slot name="items" v-bind="contextProperties"></slot>
      </AComboboxGroup>
    </AComboboxList>
  </AComboboxRoot>
</template>

<style lang="scss">
.atabix-multiselect-trigger {
  @apply grid;
  grid-template-columns: 1fr auto;
  @apply items-center;
  @apply w-full;
  @apply ring-offset-background;

  &:focus-visible {
    @apply outline-none;
    @apply border-form-border-selected;
  }
}

.atabix-multiselect-action {
  @apply shrink-0;
  @apply opacity-50;
  @apply size-4;
}

.atabix-multiselect-actions {
  @apply flex;
  @apply gap-2;
  @apply ml-2;
}

.atabix-multiselect-placeholder {
  @apply text-foreground-placeholder;
  @apply text-start;
}
</style>
